import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { announcements as mockAnnouncements } from '../../../mocks/announcements';

interface Announcement {
  id: string;
  text: string;
  link: string;
  type: string;
  sort_order?: number;
}

export default function AnnouncementBar() {
  const [announcements, setAnnouncements] = useState<Announcement[]>(mockAnnouncements);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('sort_order', { ascending: true });

      if (!error && data && data.length > 0) {
        setAnnouncements(data);
      }
    };
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    if (isPaused || announcements.length === 0) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [isPaused, announcements.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  };

  if (announcements.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-y border-amber-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="flex items-center justify-between py-3"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg">
              <i className="ri-notification-3-line text-base text-white"></i>
            </div>
            <span className="text-sm font-semibold text-gray-700 hidden sm:block whitespace-nowrap">最新公告</span>
          </div>

          <div className="flex-1 mx-4 overflow-hidden">
            <div className="relative h-6">
              {announcements.map((announcement, index) => (
                <a
                  key={announcement.id}
                  href={announcement.link}
                  className={`absolute inset-0 flex items-center transition-all duration-500 ${
                    index === currentIndex
                      ? 'opacity-100 translate-y-0'
                      : index < currentIndex
                      ? 'opacity-0 -translate-y-6'
                      : 'opacity-0 translate-y-6'
                  }`}
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap">
                    {announcement.text}
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handlePrev}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-amber-100 transition-colors cursor-pointer"
              aria-label="上一則"
            >
              <i className="ri-arrow-left-s-line text-base text-gray-600"></i>
            </button>
            <button
              onClick={handleNext}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-amber-100 transition-colors cursor-pointer"
              aria-label="下一則"
            >
              <i className="ri-arrow-right-s-line text-base text-gray-600"></i>
            </button>

            <div className="hidden sm:flex items-center gap-1 ml-2">
              {announcements.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-1.5 rounded-full transition-all cursor-pointer ${
                    index === currentIndex
                      ? 'bg-orange-500 w-4'
                      : 'bg-gray-300 hover:bg-gray-400 w-1.5'
                  }`}
                  aria-label={`跳至第 ${index + 1} 則公告`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
